import {
  WorkspaceMembersModalWrapper,
  WorkspacePage,
} from '@carboncopyco/components';
import { useBoolean } from 'ahooks';

import {
  selectOrganizationId,
  useGetWorkspaceMembersQuery,
  useGetWorkspaceQuery,
} from '@carboncopyco/data';
import type { DemoOverlay, IDemo } from '@carboncopyco/types';
import { useWorkspaceAnalytics } from '@carboncopyco/utils';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useWorkspaces } from '../../store/context';

import GiveADemoModalExtended from '../../components/GiveADemoModalExtended';
import WorkspaceLayout from '../../components/Workspaces/WorkspaceLayout';
import { useCustomNavigate } from '../../hooks/useCustomNavigate';
import { useFlagsFallback } from '../../hooks/useFlagsFallback';
import { usePlaybookPresentations } from '../../hooks/usePlaybookPresentations';

export const WorkspaceScreen = () => {
  const flags = useFlagsFallback();
  const organizationId = useSelector(selectOrganizationId);
  const { workspaceId: workspaceParamId } = useParams();
  const { activeWorkspace } = useWorkspaces();

  useEffect(() => {
    if (activeWorkspace.current.id !== workspaceParamId) {
      activeWorkspace.current = {
        id: workspaceParamId as string,
      };
    }
  }, [workspaceParamId]);

  const workspaceId =
    activeWorkspace.current.id.toString() || (workspaceParamId ?? '');

  const [showMembersModal, setShowMembersModal] = useBoolean();

  const { refetch: refetchMembers } = useGetWorkspaceMembersQuery({
    organizationId,
    workspaceId,
  });

  const { trackWorkspaceClickCloseMembersModal } = useWorkspaceAnalytics(
    'workspace_navbar_dropdown'
  );
  const { data: workspace } = useGetWorkspaceQuery({
    organizationId,
    id: workspaceId,
  });

  const [showCreatePresentation, setShowCreatePresentation] = useState(false);
  const [selectedDemo, setSelectedDemo] = useState<IDemo | DemoOverlay | null>(
    null
  );

  const {
    navigateToWorkspaces,
    navigateToTourPage,
    navigateToTourPreview,
    navigateToTourEditor,
    navigateToRecordingPreview,
    navigateToDemoPage,
    navigateToDemoPreview,
    navigateToClonePage,
    navigateToClonePageByWorkspace,
    navigateToTourPageByWorkspace,
    navigateToCreateOverlay,
    navigateToCreateOverlayInFolder,
    navigateToRecorder,
    navigateToEditor,
    navigateToTourShares,
    navigateToTourCapture,
    navigateToDemoPageByWorkspace,
    navigateToWebTourCapture,
    navigateToFolder,
    navigateToArchivedFolder,
    links,
    navigateToPlaybookPage,
  } = useCustomNavigate();

  const goToCopiedDemo = async (demoId: string, demoWorkspaceId: string) => {
    if (demoWorkspaceId) {
      navigateToDemoPageByWorkspace(demoId, demoWorkspaceId);
      return;
    }
    navigateToDemoPage(demoId, demoWorkspaceId, 'links');
  };

  const handleCreatePresentation = useCallback((demo?: IDemo | DemoOverlay) => {
    if (!demo) return;
    setShowCreatePresentation(true);
    setSelectedDemo(demo);
  }, []);

  const { handlePlayPlaybook, handlePreviewPlaybook } =
    usePlaybookPresentations({
      organizationId,
    } as { organizationId: string });

  if (!organizationId || !workspaceId) return null;

  return (
    <WorkspaceLayout>
      <div className="bg-ds-accent3-100 g-ds-accent3-200 h-full overflow-y-auto">
        <WorkspacePage
          toursShareAspectRatioEnabled={flags.toursShareAspectRatio}
          goToDemoShares={(demoId) => {
            navigateToDemoPage(String(demoId), workspaceId, 'links');
          }}
          goToCopiedDemo={(id) => goToCopiedDemo(String(id), workspaceId)}
          goToTourShares={(id) => navigateToTourShares(String(id))}
          rootPath={links.getWorkspaceLink(workspaceId)}
          goToAllWorkspaces={navigateToWorkspaces}
          handleWorkspaceMembersModal={setShowMembersModal.setTrue}
          organizationId={organizationId}
          workspaceId={workspaceId}
          goToTour={navigateToTourPage}
          goToTourPreview={(id) => navigateToTourPreview(id)}
          handleEditTour={(id) => navigateToTourEditor({ id })}
          handleCreatePresentation={handleCreatePresentation}
          goToDemo={(id) => navigateToDemoPage(String(id), workspaceId)}
          goToEditor={navigateToEditor}
          goToDemoPreview={({ id, firstUrl }) => {
            navigateToDemoPreview({
              id,
              firstUrl,
              workspaceId,
            });
          }}
          goToClone={navigateToClonePage}
          handleClonePreview={navigateToRecordingPreview}
          goToCopiedClone={navigateToClonePageByWorkspace}
          goToCopiedTour={(id, workspaceId) =>
            navigateToTourPageByWorkspace(id, workspaceId)
          }
          createOverlayPath={links.geCreateOverlayDemoLink()}
          handleCreateTourFromDemo={(id, _, __, folderId) => {
            navigateToTourCapture({ demoId: id, folderId });
          }}
          handleOpenRecorder={(_workspaceId, folderId) =>
            navigateToRecorder(folderId)
          }
          goToCreateOverlayDemoPage={navigateToCreateOverlay}
          goToCreateOverlayInFolder={navigateToCreateOverlayInFolder}
          overlayFlag
          previewArchivedDemo={({ id }) => {
            navigateToDemoPreview({
              id,
              workspaceId,
              removeDeleted: false,
            });
          }}
          navigateToWebTourCapture={navigateToWebTourCapture}
          goToFolderPage={navigateToFolder}
          goToArchivedFolderPage={navigateToArchivedFolder}
          goToPlaybook={(id) => navigateToPlaybookPage(id, workspaceId)}
          playPresentation={(play) => handlePlayPlaybook({ playSubset: play })}
          goToPlaybookPreview={async (playbookId: string) =>
            handlePreviewPlaybook(workspaceId, playbookId)
          }
        />
        {showMembersModal && workspace && (
          <WorkspaceMembersModalWrapper
            workspace={workspace}
            show={showMembersModal}
            onClose={() => {
              refetchMembers();
              setShowMembersModal.setFalse();
              trackWorkspaceClickCloseMembersModal();
            }}
            onInvite={() => {}}
            onRemove={() => {}}
          />
        )}
        {showCreatePresentation && selectedDemo && (
          <GiveADemoModalExtended
            open
            workspaceId={workspaceId}
            entity={selectedDemo}
            onPresent={() => {
              setShowCreatePresentation(false);
              setSelectedDemo(null);
            }}
            onSave={() => {
              setShowCreatePresentation(false);
              setSelectedDemo(null);
            }}
            onClose={() => {
              setShowCreatePresentation(false);
              setSelectedDemo(null);
            }}
          />
        )}
      </div>
    </WorkspaceLayout>
  );
};
